import React, { useEffect, useMemo } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Select from "components/ui/Select";
import useModalStore from "store/modals";
import { useAllSizes } from "http-hooks/size/useSize";

const SelectSizes = ({
  value,
  onChange,
  placeholder = "Search sizes...",
  isDisabled = false,
  isRequired = false,
  label = "Size",
  className = "",
  filters = {},
  showLabel = true,
  isMulti = false,
  showAddButton = true, // New prop to control plus button visibility
}) => {
  const { openModal } = useModalStore();
  
  const {
    isLoading: isLoadingAllSizes,
    data: { data: allSizes } = [],
  } = useAllSizes({
    itmsz: "",
    umid: "",
    ag_status: "1",
    noPagination: true,
    ...filters,
  });

  // Convert sizes to options for Select component - memoized for performance
  const sizeOptions = useMemo(() => allSizes?.map(size => ({
    value: size.itmszid,
    label: size.itmsz,
    itmsz: size.itmsz,
    umid: size.umid,
    ...size
  })) || [], [allSizes]);

  // Auto-select the matching option when sizes load and value is provided as simple object
  useEffect(() => {
    // If value is provided as a simple object with itmsz and umid but not as a full option
    if (value && !value.value && value.itmsz && value.umid && sizeOptions.length > 0 && onChange) {
      const matchedOption = sizeOptions.find(option => 
        String(option.itmsz) === String(value.itmsz) &&
        String(option.umid) === String(value.umid)
      );
      
      if (matchedOption && matchedOption !== value) {
        onChange(isMulti ? [matchedOption] : matchedOption);
      }
    }
  }, [value, sizeOptions, onChange, isMulti]);

  const handleAddNew = () => {
    openModal('size');
  };

  return (
    <div>
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          {showAddButton && !isDisabled && (
            <button
              type="button"
              onClick={handleAddNew}
              className="ml-2 p-1 text-xs font-bold bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200 flex items-center"
              title="Add new size"
            >
              <PlusIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <Select
        value={value}
        onChange={onChange}
        options={sizeOptions}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={isLoadingAllSizes}
        isRequired={isRequired}
        label={label}
        className={className}
        showLabel={false}
        isClearable={true}
        isSearchable={true}
        isMulti={isMulti}
      />
      {!showLabel && showAddButton && !isDisabled && (
        <button
          type="button"
          onClick={handleAddNew}
          className="absolute top-0 right-0 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
          title="Add new size"
        >
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default SelectSizes; 